<template>
  <div ref="scroller" class="tw-relative tw-pb-8 tw-overflow-x-visible tw-overflow-y-scroll">
    <ol class="tw-m-0" v-bind="attrs">
      <slot :results="results" :page="currentPage" />
    </ol>
    <div
      v-if="isLoading"
      class="tw-absolute tw-bottom-0 tw-left-1/2 -tw-translate-x-1/2 tw-inline-flex tw-justify-center tw-text-blue-300"
    >
      <AppLoading class="tw-h-2" />
    </div>
  </div>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
}
</script>

<script lang="ts" setup>
import { useInfiniteScroll } from '@vueuse/core'
import { ref, useAttrs, watch } from 'vue'
import AppLoading from './AppLoading.vue'

function buildQuery(params: Record<string, string | number | boolean | undefined | null>) {
  const filteredParams = Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => typeof value !== 'undefined' && value !== null && value !== '')
      .map(([key, value]) => [
        key,
        typeof value === 'boolean' ? (value ? '1' : '0') : String(value),
      ]),
  )
  return new URLSearchParams(filteredParams).toString()
}

const props = defineProps<{
  apiCollectionEndpoint: string
  filters: Record<string, string | number | boolean | undefined | null>
}>()
const attrs = useAttrs()

const scroller = ref<HTMLOListElement>()
const results = ref([])
const currentPage = ref(1)
const hasNextPage = ref(true)
const isLoading = ref(false)

async function loadResults(page?: number) {
  page = page ?? currentPage.value + 1
  if (page === 1 || hasNextPage.value) {
    isLoading.value = true
    try {
      const queryString = buildQuery({ ...props.filters, page })
      const res = await fetch(`${props.apiCollectionEndpoint}?${queryString}`)
      const { results: newResults, next } = await res.json()
      if (page === 1) {
        results.value = newResults
      } else {
        results.value = results.value.concat(newResults)
      }
      hasNextPage.value = next !== null
      currentPage.value = page
    } finally {
      isLoading.value = false
    }
  }
}

useInfiniteScroll(scroller, () => {
  loadResults()
})

watch(
  () => props.filters,
  () => {
    loadResults(1)
  },
  { deep: true, immediate: true },
)
</script>
